/**
 * Linkable routes: the single place that knows the URLs of the pages that
 * carry their ids in the path (a VP's Fundamentals, Add Fundamental, ...).
 *
 * Pages and links build these URLs here and never write the paths by hand.
 * The route table registers `ROUTE_PATTERNS` and redirects `LEGACY_PATHS`.
 */

import { generatePath } from 'react-router';

import {
  ADD_FUNDAMENTALS_PATH,
  EDIT_FUNDAMENTALS_PATH,
  EDIT_VALUE_PROPOSITION_PATH,
  FUNDAMENTALS_LIST_PATH,
  VALUE_PROPOSITIONS_LIST_PATH,
  VPX_USER_PROFILE_PATH,
} from '../constants/pagePath';
import { getServerMessage, SOMETHING_WENT_WRONG_MESSAGE } from '../helpers/api';

// --- Route patterns (registered in the route table) ---

export const ROUTE_PATTERNS = {
  FUNDAMENTALS: '/value-propositions/:vpId/fundamentals',
  ADD_FUNDAMENTAL: '/value-propositions/:vpId/fundamentals/add',
  EDIT_FUNDAMENTAL: '/value-propositions/:vpId/fundamentals/:fundamentalId/edit',
  EDIT_VP: '/value-propositions/:vpId/edit',
  USER_PROFILE: '/vpx/user-profile/:userId',
};

// --- Build ---

export const fundamentalsPath = (vpId) => generatePath(ROUTE_PATTERNS.FUNDAMENTALS, { vpId });

export const addFundamentalPath = (vpId) => generatePath(ROUTE_PATTERNS.ADD_FUNDAMENTAL, { vpId });

export const editFundamentalPath = (vpId, fundamentalId) =>
  generatePath(ROUTE_PATTERNS.EDIT_FUNDAMENTAL, { vpId, fundamentalId });

export const editVpPath = (vpId) => generatePath(ROUTE_PATTERNS.EDIT_VP, { vpId });

/** Another person's Person Profile Page, or my own (id-less) when no user id is given. */
export const userProfilePath = (userId) =>
  userId == null ? VPX_USER_PROFILE_PATH : generatePath(ROUTE_PATTERNS.USER_PROFILE, { userId });

// --- Parse and match ---

/**
 * Reads an id from a route param. Returns the id as a positive integer, or
 * `null` (invalid) for a missing, non-numeric, zero, negative or fractional value.
 */
export const parseRouteId = (value) => {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value > 0 ? value : null;
  }
  if (typeof value !== 'string' || !/^[1-9]\d*$/.test(value)) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) ? id : null;
};

/**
 * The user whose Person Profile Page to show: the URL's user id, or the
 * logged-in user when the URL has none ("me"). `null` when that id is invalid.
 */
export const profileUserId = (userIdParam, currentUserId) =>
  parseRouteId(userIdParam === undefined ? currentUserId : userIdParam);

/**
 * Whether data the server reports as belonging to `reportedVpId` may be shown
 * on a page whose URL names `urlVpId`. Missing or invalid ids never match.
 */
export const vpIdMatches = (urlVpId, reportedVpId) => {
  const id = parseRouteId(urlVpId);
  return id !== null && id === parseRouteId(reportedVpId);
};

// --- Opened from the Fundamentals page ---

/** Navigation state the Fundamentals page passes to the Add/Edit Fundamental forms it opens. */
export const fromFundamentalsState = (vpId) => ({ fromFundamentalsOf: vpId });

/**
 * Whether a form was opened in-app from the Fundamentals page of the VP
 * `vpId` (so that page is the previous history entry), rather than from a
 * pasted link or another page.
 */
export const openedFromFundamentals = (state, vpId) => {
  const id = parseRouteId(vpId);
  return id !== null && parseRouteId(state?.fromFundamentalsOf) === id;
};

// --- Opened from the VP list ---

/** Navigation state the VP list passes to the pages it opens (Fundamentals, Add Fundamental, Edit VP). */
export const fromVpListState = () => ({ fromVpList: true });

/**
 * Whether a page was opened in-app from the VP list (so the VP list, with its
 * filters, is the previous history entry), rather than from a pasted link or
 * another page.
 */
export const openedFromVpList = (state) => state?.fromVpList === true;

// --- Page status ---

/**
 * What a linkable page should do with the store, given:
 * - `key`: the URL's ids (e.g. "42" or "42/9"), or `null` when they are invalid;
 * - `requestedKey`: the URL whose fetches this page (this mount) has dispatched;
 * - `fetched`: whether the store's results are for this URL and finished loading;
 * - `failure`: the failed response in the store, if any;
 * - `rejected`: whether the fetched data turned out not to belong to the URL.
 *
 * Store results only count once this page's own fetch for this URL has
 * finished, so data or a failure left by an earlier visit is never taken for
 * this URL's. Returns `{ ready, leave, failure }`: show the page, or leave it
 * (with the failure to explain why, or `null` for "missing or no access").
 */
export const linkablePageStatus = ({ key, requestedKey, fetched, failure, rejected }) => {
  if (key == null) return { ready: false, leave: true, failure: null };
  if (requestedKey !== key || !fetched) return { ready: false, leave: false, failure: null };
  if (failure) return { ready: false, leave: true, failure };
  if (rejected) return { ready: false, leave: true, failure: null };
  return { ready: true, leave: false, failure: null };
};

// --- Legacy resolution (remove in the release after the linkable URLs ship) ---

/**
 * Each old id-less path maps the navigation state its page relied on to the
 * new URL, or to nothing when the ids it needs are missing.
 */
const LEGACY_RESOLVERS = {
  [FUNDAMENTALS_LIST_PATH]: (state) => {
    const vpId = parseRouteId(state?.vpId);
    return vpId && fundamentalsPath(vpId);
  },
  [ADD_FUNDAMENTALS_PATH]: (state) => {
    const vpId = parseRouteId(state?.vpId);
    return vpId && addFundamentalPath(vpId);
  },
  [EDIT_FUNDAMENTALS_PATH]: (state) => {
    const vpId = parseRouteId(state?.vpId);
    const fundamentalId = parseRouteId(state?.fundamentId);
    return vpId && fundamentalId && editFundamentalPath(vpId, fundamentalId);
  },
  [EDIT_VALUE_PROPOSITION_PATH]: (state) => {
    const vpId = parseRouteId(state?.vpId);
    return vpId && editVpPath(vpId);
  },
  // The id-less path still means "my own profile", so it never falls back to the VP list.
  // It stays a real page (not a LegacyRedirect route); the profile page forwards itself.
  [VPX_USER_PROFILE_PATH]: (state) => userProfilePath(parseRouteId(state?.userId)),
};

export const LEGACY_PATHS = Object.keys(LEGACY_RESOLVERS);

/**
 * Given an old id-less path and whatever navigation state is present, returns
 * the new URL to redirect to, or the VP list when the ids it needs are missing.
 * A trailing slash is ignored, as the (non-strict) route table matches it too.
 */
export const resolveLegacyPath = (pathname, state) =>
  LEGACY_RESOLVERS[String(pathname ?? '').replace(/\/+$/, '')]?.(state) ||
  VALUE_PROPOSITIONS_LIST_PATH;

// --- Error message selection ---

export const NOT_FOUND_OR_NO_ACCESS_MESSAGE = "This item doesn't exist or you don't have access.";

export { SOMETHING_WENT_WRONG_MESSAGE };

/**
 * Toast text for a linkable page that cannot be shown. Takes the failed fetch
 * (`{ status, body }`), or nothing when the URL's id was invalid. Prefers the
 * server's message (e.g. the limited-VP upgrade text); otherwise an invalid id
 * or a 400/401 means the item is missing or not accessible, and anything else
 * (a server error, a network failure) is a generic error.
 */
export const routeErrorMessage = (failure) => {
  const serverMessage = getServerMessage(failure);
  if (serverMessage) return serverMessage;
  if (failure == null || failure.status === 400 || failure.status === 401) {
    return NOT_FOUND_OR_NO_ACCESS_MESSAGE;
  }
  return SOMETHING_WENT_WRONG_MESSAGE;
};

